import * as Sentry from '@sentry/react';
import {
  Modal,
  ModalContent,
  Prose,
  Pushbutton,
  Pushtext
} from '@stratumn/atomic';
import to from 'await-to-js';
import { ChangePassword, GenerateKey } from 'components/ui';
import compose from 'lodash.flowright';
import { createUserSigningKey } from 'mutations';
import PropType from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';
import { withRouter } from 'react-router-dom';
import { SpanType, withSpanAsync } from 'tracing';
import { withGqlClient, withUser } from 'wrappers';

import styles from './regenerateKeyModal.style';

const INITIAL_STATE = {
  password: null,
  submitted: false,
  signingKey: null
};

export class RegenerateKeyModal extends React.PureComponent {
  static propTypes = {
    classes: PropType.object.isRequired,
    environment: PropType.object.isRequired,
    toggleModal: PropType.func.isRequired
  };

  state = INITIAL_STATE;

  handleStateChange = state => val => this.setState({ [state]: val });

  handlePasswordChange = this.handleStateChange('password');

  handleSigningKeyChange = this.handleStateChange('signingKey');

  submitUserKeys = password => () =>
    withSpanAsync('regenerateUserKey', SpanType.processing, async () => {
      const {
        environment,
        user: { account, rowId: userRowId },
        toggleModal
      } = this.props;
      const { signingKey } = this.state;

      this.setState({ submitted: true });

      // Add keys
      const { publicKey, privateKey } = signingKey.export(password);

      const [err] = await to(
        createUserSigningKey(
          environment,
          account.id,
          userRowId,
          publicKey,
          privateKey,
          !!password
        )
      );

      if (err) {
        Sentry.captureException(err);
        this.setState({ submitted: false });
      } else {
        toggleModal();
      }

      return {};
    });

  render() {
    const { classes, toggleModal } = this.props;
    const { submitted, password, signingKey } = this.state;

    return (
      <Modal handleCollapse={toggleModal} title="Regenerating Signing Key">
        <ModalContent>
          <GenerateKey
            onSigningKeyGeneration={this.handleSigningKeyChange}
            signingKey={signingKey}
          />
          <div className={classes.passwordPrompt}>
            <Prose
              light
              text="**Please secure your keys with a password.** This password can be the same as your log in password."
            />
          </div>
          <ChangePassword
            data-testid="change-password-input"
            labelConfirmPassword="Confirm key password"
            labelPassword="Key password"
            onChange={this.handlePasswordChange}
          />
        </ModalContent>
        <div className={classes.modalActionsRoot}>
          <div className={classes.modalActions}>
            <Pushtext
              data-testid="skip-button"
              disabled={submitted || !signingKey}
              mute
              onClick={this.submitUserKeys()}
              secondary
            >
              Skip this step
            </Pushtext>

            <Pushbutton
              data-testid="secure-keys-button"
              disabled={submitted || !signingKey || !password}
              onClick={this.submitUserKeys(password)}
              primary
            >
              secure keys
            </Pushbutton>
          </div>
        </div>
      </Modal>
    );
  }
}

export default compose(
  withUser,
  withRouter,
  withGqlClient,
  injectSheet(styles)
)(RegenerateKeyModal);
